import { useState, useEffect, useCallback, useMemo } from 'react';
import { contentMonitoringApi, type ContentMonitorRow, type ContentMonitorView } from '../api/contentMonitoring';
import useAppStore from '../store/useAppStore';
import { sf, type FieldKey } from '../lib/fieldPolicy';
import { TableLoadingCell, TableLoadingOverlay } from '../components/TableLoadingOverlay';
import { SortableHeader, sortRows, type SortState, type SortValue } from '../components/SortableTable';

type ViewTab = { key: ContentMonitorView; label: string };

const VIEW_TABS: ViewTab[] = [
  { key: 'deliver_user', label: '负责人' },
  { key: 'content_type', label: '内容类型' },
  { key: 'sku_type', label: '商品类型' },
];

function generateMonthOptions(): { label: string; value: string }[] {
  const opts: { label: string; value: string }[] = [];
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  for (let y = currentYear; y >= currentYear - 1; y--) {
    const maxM = y === currentYear ? currentMonth : 12;
    for (let m = maxM; m >= 1; m--) {
      const mm = String(m).padStart(2, '0');
      opts.push({ label: `${y}年${m}月`, value: `${y}-${mm}` });
    }
  }
  return opts;
}

const fmtNum = (v: number | null | undefined) => v == null ? '' : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtInt = (v: number | null | undefined) => v == null ? '' : Math.round(v).toLocaleString('en-US');
const fmtPct = (v: number | null | undefined) => v == null ? '' : (v * 100).toFixed(2) + '%';
const fmtMom = (v: number | null | undefined) => {
  if (v == null) return '';
  const pct = (v * 100).toFixed(2) + '%';
  return v > 0 ? `+${pct}` : pct;
};

interface EnrichedRow extends ContentMonitorRow {
  contentShare: number | null;
  skuShare: number | null;
  addToCartCost: number | null;
  roi: number | null;
  cpaMom: number | null;
  ctrMom: number | null;
  roiMom: number | null;
}

function enrichRows(rows: ContentMonitorRow[]): EnrichedRow[] {
  const totals = new Map<string, { contentSum: number; skuSum: number }>();
  for (const r of rows) {
    if (r.grouping_id === 7) continue;
    const key = `${r.channel}|${r.ds_month}`;
    const cur = totals.get(key) ?? { contentSum: 0, skuSum: 0 };
    cur.contentSum += r.content_cnt ?? 0;
    cur.skuSum += r.sku_cnt ?? 0;
    totals.set(key, cur);
  }

  return rows.map(r => {
    const key = `${r.channel}|${r.ds_month}`;
    const t = totals.get(key);
    const contentShare = r.content_cnt != null && t && t.contentSum > 0 ? r.content_cnt / t.contentSum : null;
    const skuShare = r.sku_cnt != null && t && t.skuSum > 0 ? r.sku_cnt / t.skuSum : null;
    const addToCartCost = r.spend != null && r.add_to_cart != null && r.add_to_cart > 0 ? r.spend / r.add_to_cart : null;
    const roi = r.gmv != null && r.spend != null && r.spend > 0 ? r.gmv / r.spend : null;
    const cpaMom = r.spend_mom != null && r.payments_mom != null && (1 + r.payments_mom) !== 0
      ? (1 + r.spend_mom) / (1 + r.payments_mom) - 1 : null;
    const ctrMom = r.clicks_mom != null && r.impressions_mom != null && (1 + r.impressions_mom) !== 0
      ? (1 + r.clicks_mom) / (1 + r.impressions_mom) - 1 : null;
    return {
      ...r,
      contentShare,
      skuShare,
      addToCartCost,
      roi,
      cpaMom,
      ctrMom,
      roiMom: null,
    };
  });
}

interface SpannedRow {
  row: EnrichedRow;
  channelSpan: number;
  monthSpan: number;
  showChannel: boolean;
  showMonth: boolean;
}

function computeSpans(rows: EnrichedRow[]): SpannedRow[] {
  const result: SpannedRow[] = rows.map(r => ({
    row: r,
    channelSpan: 1,
    monthSpan: 1,
    showChannel: false,
    showMonth: false,
  }));

  let i = 0;
  while (i < result.length) {
    let chEnd = i + 1;
    while (chEnd < result.length && result[chEnd].row.channel === result[i].row.channel) chEnd++;
    result[i].showChannel = true;
    result[i].channelSpan = chEnd - i;

    let j = i;
    while (j < chEnd) {
      let mEnd = j + 1;
      while (mEnd < chEnd && result[mEnd].row.ds_month === result[j].row.ds_month) mEnd++;
      result[j].showMonth = true;
      result[j].monthSpan = mEnd - j;
      j = mEnd;
    }
    i = chEnd;
  }

  return result;
}

interface HeaderGroup {
  label: string;
  colSpan: number;
}

interface Col {
  key: string;
  label: string;
  fmt: (row: EnrichedRow) => string;
  isMom?: boolean;
  permKey?: FieldKey;
}

const COLS: Col[] = [
  { key: 'content_cnt',     label: '素材数量',   fmt: r => fmtInt(r.content_cnt) },
  { key: 'content_share',   label: '素材占比',   fmt: r => fmtPct(r.contentShare) },
  { key: 'sku_cnt',         label: '主推SKU数',  fmt: r => fmtInt(r.sku_cnt) },
  { key: 'sku_share',       label: 'SKU数占比',  fmt: r => fmtPct(r.skuShare) },
  { key: 'spend',           label: '花费',       fmt: r => fmtNum(r.spend), permKey: 'spend' },
  { key: 'impressions',     label: '展示次数',   fmt: r => fmtInt(r.impressions) },
  { key: 'spend_share',     label: '花费占比',   fmt: r => fmtPct(r.spend_share) },
  { key: 'cpm',             label: 'CPM',        fmt: r => fmtNum(r.cpm), permKey: 'cpm' },
  { key: 'clicks',          label: '链接点击量', fmt: r => fmtInt(r.clicks) },
  { key: 'ctr',             label: 'CTR',        fmt: r => fmtPct(r.ctr) },
  { key: 'cpc',             label: 'CPC',        fmt: r => fmtNum(r.cpc), permKey: 'cpc' },
  { key: 'add_to_cart',     label: '加购次数',   fmt: r => fmtInt(r.add_to_cart) },
  { key: 'add_to_cart_rate',label: '加购转化率', fmt: r => fmtPct(r.add_to_cart_rate) },
  { key: 'add_to_cart_cost',label: '加购单价',   fmt: r => fmtNum(r.addToCartCost), permKey: 'cpa' },
  { key: 'payments',        label: '购物次数',   fmt: r => fmtInt(r.payments) },
  { key: 'pay_rate',        label: '点击支付率', fmt: r => fmtPct(r.pay_rate) },
  { key: 'cost_per_payment',label: 'CPA',        fmt: r => fmtNum(r.cost_per_payment), permKey: 'cpa' },
  { key: 'gmv',             label: '购物价值',   fmt: r => fmtNum(r.gmv), permKey: 'revenue' },
  { key: 'aov',             label: '客单价',     fmt: r => fmtNum(r.aov), permKey: 'revenue' },
  { key: 'roi',             label: 'ROI',        fmt: r => fmtNum(r.roi), permKey: 'roas' },
  { key: 'spend_mom',       label: '花费环比',   fmt: r => fmtMom(r.spend_mom), isMom: true },
  { key: 'impressions_mom', label: '展示环比',   fmt: r => fmtMom(r.impressions_mom), isMom: true },
  { key: 'clicks_mom',      label: '点击环比',   fmt: r => fmtMom(r.clicks_mom), isMom: true },
  { key: 'payments_mom',    label: '支付环比',   fmt: r => fmtMom(r.payments_mom), isMom: true },
  { key: 'cpa_mom',         label: 'CPA月环比',  fmt: r => fmtMom(r.cpaMom), isMom: true },
  { key: 'ctr_mom',         label: 'CTR月环比',  fmt: r => fmtMom(r.ctrMom), isMom: true },
  { key: 'spend_ratio',     label: '花费比率因子', fmt: r => fmtNum(r.spend_ratio_factor) },
  { key: 'gmv_ratio',       label: 'GMV比率因子', fmt: r => fmtNum(r.gmv_ratio_factor) },
];

function contentSortValue(row: EnrichedRow, key: string): SortValue {
  switch (key) {
    case 'content_share': return row.contentShare;
    case 'sku_share': return row.skuShare;
    case 'add_to_cart_cost': return row.addToCartCost;
    case 'roi': return row.roi;
    case 'cpa_mom': return row.cpaMom;
    case 'ctr_mom': return row.ctrMom;
    case 'spend_ratio': return row.spend_ratio_factor;
    case 'gmv_ratio': return row.gmv_ratio_factor;
    default: {
      const value = (row as unknown as Record<string, unknown>)[key];
      return typeof value === 'number' || typeof value === 'string' ? value : null;
    }
  }
}

/**
 * Keep channel/month groups and their total rows in their source position.
 * A metric sort only reorders detail rows inside each visual group, which
 * preserves the merged channel/month cells and avoids moving a subtotal.
 */
function sortContentRows(rows: EnrichedRow[], sort: SortState<string>): EnrichedRow[] {
  if (!sort) return rows;

  const sorted: EnrichedRow[] = [];
  let start = 0;
  while (start < rows.length) {
    const first = rows[start];
    if (first.grouping_id === 7) {
      sorted.push(first);
      start++;
      continue;
    }

    let end = start + 1;
    while (
      end < rows.length &&
      rows[end].grouping_id !== 7 &&
      rows[end].channel === first.channel &&
      rows[end].ds_month === first.ds_month
    ) {
      end++;
    }
    sorted.push(...sortRows(rows.slice(start, end), sort, contentSortValue));
    start = end;
  }
  return sorted;
}

function canSortContentMetric(col: Col, userLevel: number) {
  return !col.permKey || sf('0', col.permKey, userLevel).display === 'show';
}

const HEADER_GROUPS: HeaderGroup[] = [
  { label: '', colSpan: 4 },
  { label: '策略投入', colSpan: 2 },
  { label: '前端展示分析', colSpan: 2 },
  { label: '素材引流分析', colSpan: 3 },
  { label: '加购分析', colSpan: 3 },
  { label: '下单分析', colSpan: 3 },
  { label: '购物价值', colSpan: 3 },
  { label: '环比分析', colSpan: 6 },
  { label: '效果分析', colSpan: 2 },
];

const thStyle: React.CSSProperties = {
  whiteSpace: 'nowrap',
  fontSize: 11,
  padding: '6px 8px',
  textAlign: 'center',
  background: 'var(--surface2)',
  borderBottom: '1px solid var(--border)',
  position: 'sticky',
  top: 0,
  zIndex: 1,
};

const tdStyle: React.CSSProperties = {
  whiteSpace: 'nowrap',
  fontSize: 11,
  padding: '4px 8px',
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
  borderBottom: '1px solid var(--border)',
};

const mergedTdStyle: React.CSSProperties = {
  ...tdStyle,
  textAlign: 'center',
  verticalAlign: 'middle',
  fontWeight: 500,
  borderRight: '1px solid var(--border)',
};

export default function ContentMonitoring() {
  const { adContext, brandId } = useAppStore();
  const userLevel = adContext?.data_level ?? 1;
  const monthOptions = useMemo(() => generateMonthOptions(), []);
  const [view, setView] = useState<ContentMonitorView>('deliver_user');
  const [startMonth, setStartMonth] = useState(() => monthOptions[2]?.value ?? monthOptions[0]?.value);
  const [endMonth, setEndMonth] = useState(() => monthOptions[0]?.value);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ContentMonitorRow[]>([]);
  const [sort, setSort] = useState<SortState<string>>(null);

  const fetchData = useCallback(async () => {
    if (!startMonth || !endMonth) return;
    setLoading(true);
    try {
      const res = await contentMonitoringApi.getData({ view, start_month: startMonth, end_month: endMonth, brand_id: brandId || undefined });
      setData(res.data ?? []);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [view, startMonth, endMonth, brandId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const enriched = useMemo(() => enrichRows(data), [data]);
  const sortedRows = useMemo(() => sortContentRows(enriched, sort), [enriched, sort]);
  const spannedRows = useMemo(() => computeSpans(sortedRows), [sortedRows]);
  const dimensionLabel = VIEW_TABS.find(t => t.key === view)?.label ?? '维度';

  const toggleSort = useCallback((key: string) => {
    setSort(current => {
      if (!current || current.key !== key) return { key, direction: 'asc' };
      if (current.direction === 'asc') return { key, direction: 'desc' };
      return null;
    });
  }, []);

  return (
    <div className="page-section visible">
      <div className="data-filter-card">
        <div className="data-filter-row">
          <div className="data-filter-field" style={{ flex: '0 0 auto', width: 280 }}>
            <label className="data-filter-label">维度</label>
            <div className="page-tabs" style={{ marginBottom: 0, width: 'max-content' }}>
              {VIEW_TABS.map(t => (
                <div key={t.key} className={`page-tab ${view === t.key ? 'active' : ''}`} onClick={() => setView(t.key)}>
                  {t.label}
                </div>
              ))}
            </div>
          </div>

          <div className="data-filter-field sm">
            <label className="data-filter-label">开始月份</label>
            <select className="form-select" style={{ height: 34, paddingTop: 0, paddingBottom: 0 }} value={startMonth} onChange={event => setStartMonth(event.target.value)}>
              {monthOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <span style={{ color: 'var(--text3)', fontSize: 12, height: 34, display: 'flex', alignItems: 'center' }}>—</span>
          <div className="data-filter-field sm">
            <label className="data-filter-label">结束月份</label>
            <select className="form-select" style={{ height: 34, paddingTop: 0, paddingBottom: 0 }} value={endMonth} onChange={event => setEndMonth(event.target.value)}>
              {monthOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16, padding: 0 }}>
        <div className="table-wrap" style={{ position: 'relative' }}>
          <table style={{ borderCollapse: 'collapse', minWidth: 2200 }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, borderRight: '1px solid var(--border)' }} rowSpan={2}>渠道</th>
                <th style={{ ...thStyle, borderRight: '1px solid var(--border)' }} rowSpan={2}>日期</th>
                <th style={{ ...thStyle, borderRight: '1px solid var(--border)' }} rowSpan={2}>{dimensionLabel}</th>
                {HEADER_GROUPS.map((g, i) => {
                  if (!g.label) {
                    const offset = HEADER_GROUPS.slice(0, i).reduce((s, gg) => s + gg.colSpan, 0);
                    return Array.from({ length: g.colSpan }, (_, ci) => (
                      <SortableHeader
                        key={`e${i}_${ci}`}
                        style={thStyle}
                        rowSpan={2}
                        sortKey={COLS[offset + ci].key}
                        sort={sort}
                        onSort={toggleSort}
                        align="center"
                        disabled={!canSortContentMetric(COLS[offset + ci], userLevel)}
                      >
                        {COLS[offset + ci].label}
                      </SortableHeader>
                    ));
                  }
                  return <th key={i} style={{ ...thStyle, borderBottom: '1px solid var(--border)' }} colSpan={g.colSpan}>{g.label}</th>;
                })}
              </tr>
              <tr>
                {(() => {
                  let idx = 0;
                  return HEADER_GROUPS.map((g) => {
                    if (!g.label) { idx += g.colSpan; return null; }
                    const cells = COLS.slice(idx, idx + g.colSpan).map(c => (
                      <SortableHeader
                        key={c.key}
                        style={thStyle}
                        sortKey={c.key}
                        sort={sort}
                        onSort={toggleSort}
                        align="center"
                        disabled={!canSortContentMetric(c, userLevel)}
                      >
                        {c.label}
                      </SortableHeader>
                    ));
                    idx += g.colSpan;
                    return cells;
                  });
                })()}
              </tr>
            </thead>
            <tbody>
              {loading && spannedRows.length === 0 ? (
                <tr><td colSpan={COLS.length + 3}><TableLoadingCell /></td></tr>
              ) : spannedRows.length === 0 ? (
                <tr><td colSpan={COLS.length + 3} style={{ padding: '48px 0', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>暂无数据</td></tr>
              ) : spannedRows.map((sr, idx) => {
                  const { row, showChannel, channelSpan, showMonth, monthSpan } = sr;
                  const isTotal = row.grouping_id === 7;
                  const trStyle: React.CSSProperties | undefined = isTotal
                    ? { fontWeight: 600, background: 'var(--surface2)' }
                    : undefined;

                  return (
                    <tr key={idx} style={trStyle}>
                      {showChannel && (
                        <td rowSpan={channelSpan} style={mergedTdStyle}>{row.channel}</td>
                      )}
                      {showMonth && (
                        <td rowSpan={monthSpan} style={mergedTdStyle}>{row.ds_month?.slice(0, 7)}</td>
                      )}
                      <td style={{ ...tdStyle, textAlign: 'left', borderRight: '1px solid var(--border)' }}>
                        {row.dimension_value || '—'}
                      </td>
                      {COLS.map(c => {
                        const formatted = c.permKey ? (sf(c.fmt(row), c.permKey, userLevel).value ?? '🔒') : c.fmt(row);
                        const rawVal = c.isMom ? parseMomValue(row, c.key) : null;
                        const color = c.isMom && rawVal != null
                          ? (rawVal > 0 ? '#16a34a' : rawVal < 0 ? '#dc2626' : undefined)
                          : undefined;
                        return (
                          <td key={c.key} style={{ ...tdStyle, color }}>
                            {formatted}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
            </tbody>
          </table>
          {loading && spannedRows.length > 0 && <TableLoadingOverlay />}
        </div>
      </div>
    </div>
  );
}

function parseMomValue(row: EnrichedRow, key: string): number | null {
  switch (key) {
    case 'spend_mom': return row.spend_mom;
    case 'impressions_mom': return row.impressions_mom;
    case 'clicks_mom': return row.clicks_mom;
    case 'payments_mom': return row.payments_mom;
    case 'cpa_mom': return row.cpaMom;
    case 'ctr_mom': return row.ctrMom;
    default: return null;
  }
}
